import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  Grid,
  MenuItem,
  TextField,
} from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'

import { useParams, useHistory } from 'react-router-dom'
import BaseFilterModal from '../../../../../components/hooks/BaseFilterModal'
import filterStylesComponents from '../../../../../assets/filterComponentsStyles'
import {
  edit,
  editServiceItem,
  fetchServiceTypesForLaundry,
  updateServiceItem,
} from '../../PartnerAttendanceActions'
import NumberFormatCustom from '../../../../components/NumberFormat'

const EditItem = ({}) => {
  const serviceItem = useSelector(
    state => state.partnerAttendances.editServiceItem
  )
  const showingAttendanceFilter = useSelector(
    state => state.partnerAttendances.showingAttendanceFilter
  )
  const details = useSelector(
    state => state.partnerAttendances.showingAttendance
  )

  const laundryId =
    details?.laundry?.id || details?.laundry_id || serviceItem?.laundry_id

  const { serviceItem: serviceItemId, id } = useParams()

  const [serviceTypes, setServiceTypes] = useState([])
  const [form, setForm] = useState({
    service_type_id: '',
    bar_code: '',
    options: { type: '', base: '', altura: '', description: '', diametro: '' },
    quantity: '',
  })

  const dispatch = useDispatch()
  const history = useHistory()

  const filterStyles = filterStylesComponents()

  const fetchEditItem = async () => {
    dispatch(editServiceItem(serviceItemId))
  }

  const handleClose = () => {
    history.goBack()
  }

  const fetchServiceTypes = async () => {
    const resp = await dispatch(fetchServiceTypesForLaundry(laundryId))
    setServiceTypes(Array.isArray(resp) ? resp : [])
  }

  // Garante que o serviço atual do item aparece no select, mesmo que não
  // esteja mais configurado como mensal para a lavanderia
  const serviceTypeOptions = useMemo(() => {
    const current = serviceItem?.serviceType
    if (!current?.id || serviceTypes.some(r => r.id == current.id))
      return serviceTypes
    return [current, ...serviceTypes]
  }, [serviceTypes, serviceItem])

  const measureTypeId = useMemo(() => {
    const selected = serviceTypeOptions.find(r => r.id == form?.service_type_id)
    return (
      selected?.measure_type_id ?? serviceItem?.serviceType?.measure_type_id
    )
  }, [serviceTypeOptions, form?.service_type_id, serviceItem])

  const handleChangeServiceType = e => {
    const { value } = e.target
    const newMeasureTypeId = serviceTypeOptions.find(
      r => r.id == value
    )?.measure_type_id

    if (newMeasureTypeId == measureTypeId) {
      setForm({ ...form, service_type_id: value })
      return
    }

    // Mudou o tipo de medida: volta aos valores originais do item se for o
    // mesmo tipo de medida dele, senão limpa as medidas para serem preenchidas
    if (newMeasureTypeId == serviceItem?.serviceType?.measure_type_id) {
      setForm({
        ...form,
        service_type_id: value,
        quantity: serviceItem?.quantity,
        options: {
          ...serviceItem?.options,
          description: form?.options?.description,
        },
      })
      return
    }

    setForm({
      ...form,
      service_type_id: value,
      quantity: '',
      options: {
        type: '',
        base: '',
        altura: '',
        diametro: '',
        description: form?.options?.description,
      },
    })
  }

  const init = () => {
    setForm({
      service_type_id: serviceItem?.serviceType?.id || '',
      bar_code: serviceItem?.bar_code,
      options: serviceItem?.options,
      quantity: serviceItem?.quantity,
    })
  }

  const handleChangeOptions = e => {
    const { name, value } = e.target

    setForm({
      ...form,
      options: {
        ...form?.options,
        [name]: value,
      },
    })
  }

  const handleChange = e => {
    const { name, value } = e.target

    setForm({
      ...form,
      [name]: value,
    })
  }

  const toNumber = value => {
    const number = parseFloat(String(value ?? '').replace(',', '.'))
    return Number.isNaN(number) ? 0 : number
  }

  // A API exige sempre type/base/altura/diametro numéricos (0 quando não se
  // aplica) e grava o objeto inteiro, substituindo o que estava salvo
  const buildOptions = () => {
    const isSquareMeter = measureTypeId == 2
    const type = isSquareMeter ? toNumber(form?.options?.type) || 1 : 1
    const isRectangular = isSquareMeter && type == 1
    const isCircular = isSquareMeter && type != 1

    return {
      ...form?.options,
      type,
      base: isRectangular ? toNumber(form?.options?.base) : 0,
      altura: isRectangular ? toNumber(form?.options?.altura) : 0,
      diametro: isCircular ? toNumber(form?.options?.diametro) : 0,
    }
  }

  const handleSubmitForm = async e => {
    e.preventDefault()
    const resp = await dispatch(
      updateServiceItem(serviceItemId, { ...form, options: buildOptions() })
    )

    if (!resp?.data) return

    await dispatch(edit(id, showingAttendanceFilter))
    handleClose()
  }

  useEffect(() => {
    if (!serviceItem) return
    init()
  }, [serviceItem])

  useEffect(() => {
    fetchEditItem()
  }, [serviceItemId])

  useEffect(() => {
    if (!laundryId) return
    fetchServiceTypes()
  }, [laundryId])

  return (
    <BaseFilterModal
      open
      title={`Editar Item`}
      handleOpen={handleClose}
      hiddenHelpButton
    >
      <Grid item xs={12} style={{ width: '550px' }}>
        <form onSubmit={handleSubmitForm}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Serviço"
                name="service_type_id"
                value={
                  serviceTypeOptions.length ? form?.service_type_id || '' : ''
                }
                onChange={handleChangeServiceType}
                required
              >
                {serviceTypeOptions.map(serviceType => (
                  <MenuItem value={serviceType.id} key={serviceType.id}>
                    {serviceType.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Etiqueta"
                value={form?.bar_code || ''}
                name="bar_code"
                onChange={handleChange}
                required
              />
            </Grid>

            {measureTypeId != 2 && (
              <Grid item xs={12} md={6}>
                <Grid container justifyContent="flex-end">
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Quantidade"
                      name="quantity"
                      value={form?.quantity}
                      onChange={e =>
                        setForm({ ...form, quantity: e.target.value })
                      }
                      required
                      InputProps={{
                        inputComponent: NumberFormatCustom,
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}

            {measureTypeId == 2 && (
              <Grid item xs={12}>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Tipo de medida"
                      required
                      value={form?.options?.type}
                      key={`select-type-${form?.options?.type}`}
                      onChange={handleChangeOptions}
                      name="type"
                      select
                    >
                      <MenuItem value={'1'} key={'1'}>
                        Quadrado
                      </MenuItem>
                      <MenuItem value={'2'} key={'2'}>
                        Redondo
                      </MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    {form?.options?.type == 1 && (
                      <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={6}>
                          {/* <TextField fullWidth label="Base" required /> */}
                          <TextField
                            label="Base"
                            value={form?.options?.base}
                            onChange={e =>
                              setForm({
                                ...form,
                                options: {
                                  ...form.options,
                                  base: e.target.value,
                                },
                              })
                            }
                            required
                            InputProps={{
                              inputComponent: NumberFormatCustom,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Altura"
                            value={form?.options?.altura}
                            onChange={e =>
                              setForm({
                                ...form,
                                options: {
                                  ...form.options,
                                  altura: e.target.value,
                                },
                              })
                            }
                            required
                            InputProps={{
                              inputComponent: NumberFormatCustom,
                            }}
                          />
                        </Grid>
                      </Grid>
                    )}

                    {form?.options?.type == 2 && (
                      <Grid
                        container
                        spacing={4}
                        alignItems="center"
                        justifyContent="flex-end"
                      >
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Diametro"
                            required
                            name="diametro"
                            onChange={handleChangeOptions}
                            value={form?.options?.diametro}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                label="Observações"
                value={form?.options?.description || ''}
                onChange={handleChangeOptions}
                name="description"
                fullWidth
                multiline
              />
            </Grid>

            <Grid item xs={12}>
              <Grid
                container
                spacing={2}
                justifyContent="flex-end"
                alignItems="center"
                style={{ marginTop: '1em' }}
                className={filterStyles.areaButtons}
              >
                <Grid item>
                  <Button className={filterStyles.btnMoreFilters}>
                    Fechar
                  </Button>
                </Grid>
                <Grid item>
                  <Button type="submit" className={filterStyles.btnSalvar}>
                    Salvar
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </BaseFilterModal>
  )
}

export default EditItem
